import { observer } from "mobx-react-lite";
import { useInjection } from "../../../react-binding";
import HistogramService from "../../../services/MathServices/Histogram.service";
import { useCallback, useEffect, useState } from "react";
import { PrintHeader } from "../../main/PrintHeader";
import { BankService } from "../../../services/Bank/BankService";
import UserInfo from "../../../services/UserInfo.service";
import { PrintFooter } from "../../main/PrintFooter";
import { ImportExport } from "../partials/ImportExport";
import { Select } from "../partials/Select";
import { InputWithSlider } from "../partials/InputWithSlider";
import { getPercentil } from "../../../helpers/helpers";
import { HistogramData, Percentiles } from "../../../constants/types";
import { action } from "mobx";
import { Bar } from "react-chartjs-2";
import './Histogram.scss';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
  } from 'chart.js'
import { Tooltip as ReactTooltip } from "react-tooltip";
import { InfoCircle } from "react-bootstrap-icons";
import { PrintButton } from "../../main/PrintButton";
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
  )

  const labels = ['10%', '20%', '30%', 'April', 'May', 'June', 'July'];
const data = {
  labels: labels,
  datasets: [{
    axis: 'y',
    label: 'My First Dataset',
    data: [65, 59, 80, 81, 56, 55, 40],
    fill: false,
    backgroundColor: [
      'rgba(255, 99, 132, 0.2)',
      'rgba(255, 159, 64, 0.2)',
      'rgba(255, 205, 86, 0.2)',
      'rgba(75, 192, 192, 0.2)',
      'rgba(54, 162, 235, 0.2)',
      'rgba(153, 102, 255, 0.2)',
      'rgba(201, 203, 207, 0.2)'
    ],
    borderColor: [
      'rgb(255, 99, 132)',
      'rgb(255, 159, 64)',
      'rgb(255, 205, 86)',
      'rgb(75, 192, 192)',
      'rgb(54, 162, 235)',
      'rgb(153, 102, 255)',
      'rgb(201, 203, 207)'
    ],
    borderWidth: 1
  }]
};
const config = {
    type: 'bar',
    data: data,
    options: {
      indexAxis: 'y',
      scales: {
        x: {
          beginAtZero: true
        }
      }
    }
  };

export const Histogram = observer(() => {
    const histogramService = useInjection(HistogramService);
    const bankService = useInjection(BankService) as BankService;
    const userInfoService = useInjection(UserInfo);
    const [showPrintHeader, setShowPrintHeader] = useState<boolean>(false);
    const [percentiles, setPercentiles] = useState<Percentiles | null>(null);
    const [graphResult, setGraphResult] = useState<{ labels: string[]; data: number[];}>();
    const [graphLabels, setGraphLabels] = useState<string[]>();
    const [auxData, setAuxData] = useState<{totalCount: number, lossCount: number}>();
    const [computing, setComputing] = useState<boolean>(false);
    const [lastUsedInputData, setLastUsedInputData] = useState<HistogramData>();
    const [dirtyInputs, setDirtyInputs] = useState<boolean>(false);

    // This is a hack to ensure tha the print header is shown after the page is loaded
    useEffect(() => {
        setTimeout(() => {
            setShowPrintHeader(true);
        }, 1000);
    }, []);

    const [lossSimulationsTooltipText, setLossSimulationsTooltipText] = useState<string>("");

    const checkDirtyInputs = useCallback(() => {
        setTimeout(() => {
            const currentData = JSON.parse(JSON.stringify(userInfoService.histogramData));
            const lastData = lastUsedInputData ? JSON.parse(JSON.stringify(lastUsedInputData)): null;
            if (lastData) {
                if (currentData.beginningYear !== lastData.beginningYear) {
                    setDirtyInputs(true);
                    return;
                }
                if (currentData.endYear !== lastData.endYear) {
                    setDirtyInputs(true);
                    return;
                }
                if (currentData.investmentType !== lastData.investmentType) {
                    setDirtyInputs(true);
                    return;
                }
                if (currentData.accountForInflation !== lastData.accountForInflation) {
                    setDirtyInputs(true);
                    return;
                }
                if (currentData.due !== lastData.due) {
                    setDirtyInputs(true);
                    return;
                }
                if (currentData.shares !== lastData.shares) {
                    setDirtyInputs(true);
                    return;
                }
                if (currentData.bonds !== lastData.bonds) {
                    setDirtyInputs(true);
                    return;
                }
                if (currentData.rest !== lastData.rest) {
                    setDirtyInputs(true);
                    return;
                }
                setDirtyInputs(false);
            }
        }, 500);
    }, [userInfoService.histogramData, lastUsedInputData]);

    const setSharesOfPortfolio = action((value: {
        shares: number,
        bonds: number,
        rest: number
    }) => {
        userInfoService.histogramData.shares = value.shares.toString();
        userInfoService.histogramData.bonds = value.bonds.toString();
        userInfoService.histogramData.rest = value.rest.toString();
    });

    const compute = useCallback(() => {
        setComputing(true);
        console.log("Requesting histogram data:", userInfoService.histogramData);
        setLastUsedInputData(JSON.parse(JSON.stringify(userInfoService.histogramData)));
        histogramService.getHistogramData(
            userInfoService.histogramData
        ).then(result => {
            const key = Object.keys(result[0])[0];
            const sanitizedResult = result.map((item: {[key: string]: number}) => Number(item[key])) as number[];
            const sorted = sanitizedResult.sort((a,b) => a - b);

            const percentiles = {
                perc10: getPercentil(sorted, 10),
                perc20: getPercentil(sorted, 20),
                perc30: getPercentil(sorted, 30),
                perc40: getPercentil(sorted, 40),
                perc50: getPercentil(sorted, 50), // median
                perc60: getPercentil(sorted, 60),
                perc70: getPercentil(sorted, 70),
                perc80: getPercentil(sorted, 80),
                perc90: getPercentil(sorted, 90),

                kvartil1: getPercentil(sorted, 25),
                kvartil2: getPercentil(sorted, 50),
                kvartil3: getPercentil(sorted, 75),

                perc1: getPercentil(sorted, 1),
                perc99: getPercentil(sorted, 99)
            }
            setPercentiles(percentiles);

            const minYield = sanitizedResult[0];
            const maxYield = sanitizedResult[sanitizedResult.length - 1];
            const step = (maxYield - minYield) / 10;

            const steps = [];
            for (let i = 1; i <= 10; i++) {
                steps.push(sanitizedResult[0] + i*step);
            }
            const graphResult = steps.map((item) => {
                return {
                    breakpoint: item,
                    count: sanitizedResult.findIndex(resultsItem => {
                        const result = resultsItem >= (Math.min(item, sanitizedResult[sanitizedResult.length -1]));
                        return result;
                    })
                }
            })

            const counts = graphResult.map((item, index) => {
                return item.count - (index > 0 ? graphResult[index-1].count : 0)
            });
            
            const percentsOfCounts = counts.map(count => Math.round((count/result.length)*10000)/100);

            const breakpoints = graphResult.map((item, index) => {
                return item.breakpoint
            });

            const graphLabels = graphResult?.map((item, index) => {
                let labelResult = ``;
                if (index === 0) {
                    labelResult += `${(Math.round(Number(sorted[0]) * 1000)/10).toString().replace(".", ",")} % až `
                }
                if (index > 0) {
                    labelResult += `${(Math.round(graphResult[index - 1].breakpoint * 1000)/10).toString().replace(".", ",")} % až `
                }
                labelResult += (Math.round(item.breakpoint * 1000)/10).toString().replace(".", ",") + " %";
                return labelResult;
            });

            const graphData = {
                labels: graphLabels,
                data: percentsOfCounts
            }

            setGraphLabels(graphLabels);
            setGraphResult(graphData);
            setAuxData({
                totalCount: result.length,
                lossCount: Math.round((sanitizedResult.findIndex(item => item >= 0) / result.length)*10000)/100
            });
            setComputing(false);
            setDirtyInputs(false);
        });
    }, [histogramService, userInfoService.histogramData])

    const renderGraph = useCallback((fixedSize?: boolean) => {
        const options = {
            scales: {
                y: {
                    title: {
                        display: true,
                        text: "Četnost v %"
                    }
                },
                x: {
                    title: {
                        display: true,
                        text: "Interval průměrných ročních výnosů"
                    }
                }
            },
            plugins: {
              legend: {
                display: true,
              },
              tooltip: {
                callbacks: {
                  label: function (data: any) {
                    return "Historická četnost případů: " + (data.formattedValue).toString().replace(".", ",") + " %";
                  },
                },
              },
              datalabels: {
                formatter: function (value: any) {
                   //custom money icon
                  return "₺" + new Intl.NumberFormat("tr-TR").format(value);
                },
                color: "white",
                font: {
                  weight: 'bold' as const,
                  size:14,
                  family: 'poppins'
                },
              },
            },
        };

// The following colors will be used sequentially for the chart bars
// const backgroundColors = ["#53D9D9", "#002B49", "#0067A0"];

        const backgroundColors = (ctx: any) => {
        const canvas = ctx.chart.canvas;
        const ctx2 = canvas.getContext("2d");
        
        const r = document.querySelector(':root') as HTMLElement;
        const primaryColor = r.style.getPropertyValue('--primary-color');
        const secondaryColor = r.style.getPropertyValue('--secondary-color');

        // Create a linear gradient
        const gradient = ctx2.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, primaryColor ? primaryColor : "#53D9D9");  // Start color
        gradient.addColorStop(1, secondaryColor ? secondaryColor : "#0067A0");  // End color
        
        return gradient;
        };

        const data = {
        labels: graphResult?.labels,
        datasets: [
            {
            label:"Historická četnost případů v %",
            data: graphResult?.data,
            backgroundColor: backgroundColors,
//                backgroundColor: backgroundColors,
            borderWidth: 1,
            },
        ],
        };
    
        if (!fixedSize) {
        return <Bar data={data} options={options} />
        } else {
        return <span>
            <Bar data={data} options={options} width={"650px"} height={"500px"}/>
        </span>
        }

    }, [graphResult]);

    const setYearsAndDue = action((beginningYear: number, endYear: number) => {
        const due = Number(userInfoService.histogramData.due);

        if (endYear - beginningYear < 3) {
            // We don't know which one has changed, so we need to check first
            // Then the changed value will "push" the other one unless it's not possible
            // because of the min/max year boundaries

            const maxYear = 2025;
            const minYear = 1879;

            const currentBeginningYear = Number(userInfoService.histogramData.beginningYear);

            const isBeginningYearChanged = beginningYear !== currentBeginningYear;

            if (isBeginningYearChanged) {
                const newEndYear = (Math.min(beginningYear + 3, maxYear)).toString();
                const newBeginningYear = Math.max(Number(newEndYear) - 3, minYear).toString();
                userInfoService.histogramData.endYear = newEndYear;
                userInfoService.histogramData.beginningYear = newBeginningYear;
                console.error(userInfoService.histogramData.beginningYear, userInfoService.histogramData.endYear);
            } else {
                const newBeginningYear = (Math.max(endYear - 3, minYear)).toString();
                const newEndYear = Math.min(Number(newBeginningYear) + 3, maxYear).toString();
                userInfoService.histogramData.endYear = newEndYear;
                userInfoService.histogramData.beginningYear = newBeginningYear;
                console.error(userInfoService.histogramData.beginningYear, userInfoService.histogramData.endYear);
            }

        } else {
            userInfoService.histogramData.beginningYear = beginningYear;
            userInfoService.histogramData.endYear = endYear;
        }

        if (due > endYear - beginningYear) {
            userInfoService.histogramData.due = Math.max((endYear - beginningYear), 3).toString();
        }

        checkDirtyInputs();

        /*
        EndYear can't be lower than beginning year + 3
        Beginning year can't be higher than end year - 3
        due will follow accordingly
        */
    });

    /*
     What will be here:
     - inputs:
        - beginning year
        - end year
        - switch for displaying advanced statistics
        - investment type
        - account for inflation switch
        - due
        - share of shares
        - share of bonds
        - rest (will be calculated automatically)
    */
    return (<>
        <div className="histogram app-screen display-noneaa print-hiddenaa">
            {
                showPrintHeader &&
                <PrintHeader 
                    title= {`${bankService.dataService.wordPressUIData.annuityCalcName ? bankService.dataService.wordPressUIData.annuityCalcName : 'Historické výnosy aktiv'}`}
                    subtitle= {userInfoService.general.name}
                    hideDetails = {true}
                />
        }
            <PrintFooter
                hideDetails = {false}
            />
{
//            <ImportExport/>
}
            <h2 className="mx-auto block w-full font-bold text-[24px]">
                Rozdělení historických výnosů portfolií ze základních tříd aktiv
            </h2>
            <div className="inputs-wrapper small-input simple-hypo-input-section  print-hidden">
                <div className="simple-label flex">
                    Počáteční rok datové sady
                </div>
                <input
                    className="simple-input border-2 border-gray-300 rounded-md"
                    type="number"
                    value={userInfoService.histogramData.beginningYear}
                    onChange={(e) => {
                        setYearsAndDue(Number(e.target.value), Number(userInfoService.histogramData.endYear));
                    }}
                    step={1}
                    min={1879}
                    max={2020}
                />
            </div>

            <div className="inputs-wrapper small-input simple-hypo-input-section  print-hidden">
                <div className="simple-label flex">
                    Konečný rok datové sady
                </div>
                <input
                    className="simple-input border-2 border-gray-300 rounded-md"
                    type="number"
                    value={userInfoService.histogramData.endYear}
                    onChange={(e) => {
                        setYearsAndDue(Number(userInfoService.histogramData.beginningYear), Number(e.target.value));
                    }}
                    step={1}
                    min={1882}
                    max={2025}
                />
            </div>

            <div className="selects-wrapper  print-hidden">
                <div className="select-section">
                    <span className="simple-label flex">
                        Typ investice
                        <InfoCircle className="tooltip-icon" data-tooltip-id="type-tooltip"/>
                    </span>
                    <Select
                        options={[
                            {
                                value: 'oneTime',
                                label: 'jednorázová'
                            },
                            {
                                value: 'regular',
                                label: 'pravidelná'
                            }
                        ]}
                        observable={userInfoService.histogramData}
                        field="investmentType"
                        onChangeCallback={() => {checkDirtyInputs()}}
                    />
                </div>

                <div className="select-section  print-hidden">
                    <span className="simple-label flex">
                        Zobrazení výsledků
                        <InfoCircle className="tooltip-icon" data-tooltip-id="results-type-tooltip"/>
                    </span>
                    <Select
                        options={[
                            {
                                value: "false",
                                label: 'nominálně'
                            },
                            {
                                value: "true",
                                label: 'reálně'
                            }
                        ]}
                        observable={userInfoService.histogramData}
                        field="accountForInflation"
                        onChangeCallback={() => {checkDirtyInputs()}}
                    />
                </div>
            </div>
            <div className="inputs-wrapper simple-hypo-input-section print-hidden">
                <div className="simple-label flex">
                    Investiční horizont
                    <InfoCircle className="tooltip-icon" data-tooltip-id="horizont-tooltip"/>
                </div>
                <InputWithSlider
                    observable={userInfoService.histogramData}
                    field='due'
                    placeholder="Investiční horizont"
                    min={3}
                    max={Math.min(50, Number(userInfoService.histogramData.endYear) - Number(userInfoService.histogramData.beginningYear))}
                    step={1}
                    showDecimals={false}
                    textAfterInput="let"
                    disabled={
                        Math.min(30, Number(userInfoService.histogramData.endYear) - Number(userInfoService.histogramData.beginningYear)) === 3
                    }
                    onChangeCallback={() => {checkDirtyInputs()}}
                />
            </div>
            <div className="inputs-wrapper simple-hypo-input-section print-hidden">
                <div className="simple-label flex">
                    Podíl akcií v %
                    <InfoCircle className="tooltip-icon" data-tooltip-id="shares-tooltip"/>
                </div>
                <InputWithSlider
                    observable={userInfoService.histogramData}
                    field='shares'
                    placeholder="Podíl akcií"
                    min={0}
                    max={100}
                    step={5}
                    showDecimals={false}
                    disableNumericalInput={true}
                    onChangeCallback={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newValue = Number(e.target.value);
                        let bonds = Number(userInfoService.histogramData.bonds);
                        if (newValue + bonds > 100) {
                            bonds = 100 - newValue;
                        }
                        const rest = 100 - newValue - bonds;
                        setSharesOfPortfolio({
                            shares: newValue,
                            bonds,
                            rest
                        })
                        checkDirtyInputs();
                    }}
                    textAfterInput=" %"
                />
            </div>
            <div className="inputs-wrapper simple-hypo-input-section  print-hidden">
                <div className="simple-label flex">
                    Podíl dluhopisů v %
                    <InfoCircle className="tooltip-icon" data-tooltip-id="bonds-tooltip"/>
                </div>
                <InputWithSlider
                    observable={userInfoService.histogramData}
                    field='bonds'
                    placeholder="Podíl dluhopisů"
                    min={0}
                    max={100}
                    step={5}
                    showDecimals={false}
                    disableNumericalInput={true}
                    onChangeCallback={(e: React.ChangeEvent<HTMLInputElement>) => {
                        let newValue = Number(e.target.value);
                        if (newValue % 5 !== 0) {
                            newValue = newValue - newValue % 5;
                        }
                        let shares = Number(userInfoService.histogramData.shares);
                        if (newValue + shares > 100) {
                            shares = 100 - newValue;
                        }
                        const rest = 100 - newValue - shares;
                        setSharesOfPortfolio({
                            shares,
                            bonds: newValue,
                            rest
                        })
                        checkDirtyInputs();
                    }}
                    textAfterInput=" %"
                />
            </div>
            <div className="inputs-wrapper simple-hypo-input-section  print-hidden">
                <div className="simple-label flex">
                    Zbylo na peněžní trh
                    <InfoCircle className="tooltip-icon" data-tooltip-id="rest-tooltip"/>
                </div>
                <InputWithSlider
                    observable={userInfoService.histogramData}
                    field='rest'
                    placeholder="Peněžní trh"
                    min={0}
                    max={100}
                    step={1}
                    showDecimals={false}
                    onChangeCallback={() => {checkDirtyInputs()}}
                    textAfterInput=" %"
                    disabled={true}
                />
            </div>

            <div className="action-button-wrapper print-hidden">
                <button
                    className = "action-button"
                    onClick={() => compute()}>
                    Počítej
                </button>
                <PrintButton disabled={(computing || !graphResult)}/>
            </div>
            {graphResult && graphLabels && lastUsedInputData ? <>
                <div className={`results-section relative flex flex-col ${computing ? "computing": ""} ${dirtyInputs ? 'dirty' : ''}`}>

                    {computing ?
                        <div className="spinnerLoader absolute print-hidden">
                            <div className="spinner">
                                <div className="animation"></div>
                            </div>
                        </div>
                        : <div className={`dirty-label print-hidden ${dirtyInputs ? '' : '!hidden'}`}>
                            Změna vstupů
                            <div className="sub-label">
                                (skiskni "Počítej")
                            </div>
                        </div>
                    }
                    <div className="flex flex-row justify-evenly w-full print-hidden flex-important">
                        <div className="result">
                            <div className="result-label" data-tooltip-id="nr-of-simulations-tooltip">
                                Počet simulací
                            </div>
                            <div className="result-value">
                                {auxData?.totalCount}
                            </div>
                        </div>
                        <div className="result  print-hidden">
                            <div className="result-label" data-tooltip-id="median-tooltip">
                                Medián výnosu
                            </div>
                            <div className="result-value">
                                { (Math.round (Number(percentiles?.perc50) * 10000) / 100).toString().replace(".", ",")} %
                            </div>
                        </div>
                        <div className="result  print-hidden">
                            <div className="result-label" data-tooltip-id="loss-simulations-tooltip">
                                Ztrátových simulací
                            </div>
                            <div className="result-value">
                                {auxData?.lossCount.toString().replace(".", ",")} %
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row space-evenly w-full  print-hidden flex-important">
                        <div className="result">
                            <div className="result-label" data-tooltip-id="percentile-range-tooltip">
                                Percentilové rozpětí
                            </div>
                            <div className="result-value">
                                { (Math.round (Number(percentiles?.perc1) * 10000) / 100).toString().replace(".", ",")} % až { (Math.round (Number(percentiles?.perc99) * 10000) / 100).toString().replace(".", ",")} %
                            </div>
                        </div>
                        <div className="result">
                            <div className="result-label" data-tooltip-id="ten-percentile-range-tooltip">
                                Decilové rozpětí
                            </div>
                            <div className="result-value">
                                { (Math.round (Number(percentiles?.perc10) * 10000) / 100).toString().replace(".", ",")} % až { (Math.round (Number(percentiles?.perc90) * 10000) / 100).toString().replace(".", ",")} %
                            </div>
                        </div>
                        <div className="result">
                            <div className="result-label" data-tooltip-id="quartile-range-tooltip">
                                Kvartilové rozpětí
                            </div>
                            <div className="result-value">
                                { (Math.round (Number(percentiles?.kvartil1) * 10000) / 100).toString().replace(".", ",")} % až { (Math.round (Number(percentiles?.kvartil3) * 10000) / 100).toString().replace(".", ",")} %
                            </div>
                        </div>
                    </div>

                    <div className="print-stuff-wrapper print-visible">
                        <div className="w-full inputs-grid">
                            <div className="input-wrapper">
                                <span className="input-label">Typ investice:</span> {lastUsedInputData.investmentType === "oneTime" ? "jednorázová" : "pravidelná"}
                            </div>
                            <div className="input-wrapper">
                            <span className="input-label">Zobrazení výsledků:</span> {lastUsedInputData.accountForInflation === "true" ? "reálně" : "nominálně"}
                            </div>
                            <div className="input-wrapper">
                                <span className="input-label">Investiční horizont:</span> {lastUsedInputData.due} let
                            </div>
                            <div className="input-wrapper">
                                <span className="input-label">Podíl akcií:</span> {lastUsedInputData.shares} %
                            </div>
                            <div className="input-wrapper">
                            <span className="input-label">Podíl dluhopisů:</span> {lastUsedInputData.bonds} %
                            </div>
                            <div className="input-wrapper">
                                <span className="input-label">Zbylo na peněžní trh:</span> {lastUsedInputData.rest} %
                            </div>
                        </div>

                        <div className="w-full results-section">
                            <div className="result-wrapper">
                                <span className="input-label">Počet simulací:</span> {auxData?.totalCount}
                            </div>
                            <div className="result-wrapper">
                                <span className="input-label">Medián výnosu:</span> {(Math.round (Number(percentiles?.perc50) * 10000) / 100).toString().replace(".", ",")} %
                            </div>
                            <div className="result-wrapper">
                                <span className="input-label">Ztrátových simulací:</span> {auxData?.lossCount.toString().replace(".", ",")} %
                            </div>

                            <div className="results-label table-label">
                                Rozpětí výnosů
                            </div>

                            <div className="table-wrapper">
                                <table className="results-table">
                                    <thead>
                                        <tr>
                                            <th>Percentil</th>
                                            <th>Dolní hranice</th>
                                            <th>Horní hranice</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1%</td>
                                            <td>{(Math.round (Number(percentiles?.perc1) * 10000) / 100).toString().replace(".", ",")} %</td>
                                            <td>{(Math.round (Number(percentiles?.perc99) * 10000) / 100).toString().replace(".", ",")} %</td>
                                        </tr>
                                        <tr>
                                            <td>10%</td>
                                            <td>{(Math.round (Number(percentiles?.perc10) * 10000) / 100).toString().replace(".", ",")} %</td>
                                            <td>{(Math.round (Number(percentiles?.perc90) * 10000) / 100).toString().replace(".", ",")} %</td>
                                        </tr>
                                        <tr>
                                            <td>25%</td>
                                            <td>{(Math.round (Number(percentiles?.kvartil1) * 10000) / 100).toString().replace(".", ",")} %</td>
                                            <td>{(Math.round (Number(percentiles?.kvartil3) * 10000) / 100).toString().replace(".", ",")} %</td>
                                        </tr>
                                        <tr>
                                            <td>Medián</td>
                                            <td colSpan={2}>{(Math.round (Number(percentiles?.perc50) * 10000) / 100).toString().replace(".", ",")} %</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>
                    <div className={`graph-wrapper ${computing ? "computing": ""}`}>
                        {renderGraph(true)}
                    </div>

                    <div className="disclaimer">
                        {`
                            K výpočtům jsou využita měsíční data z vývoje finančních aktiv v USD bez zohlednění nákladů na
                            sjednání a správu investic. Simulace jsou provedeny za období let ${lastUsedInputData.beginningYear} - ${lastUsedInputData.endYear}.`}
                            <b> Upozornění: Jedná
                            se pouze o zobrazení historických výsledků, které nejsou zárukou budoucího vývoje a nelze z
                            nich na budoucí vývoj ani usuzovat. Investice obsahují riziko kolísání hodnoty a návratnost
                            původně investovaných prostředků není nikdy zaručena.
                        </b>
                    </div>
                </div>
            </> : <>
                {computing &&
                    <div className="spinnerLoader loader-w-full">
                        <div className="spinner">
                            <div className="animation"></div>
                        </div>
                    </div>
                }
            </>}
        </div>
        <>
            <ReactTooltip
                id="horizont-tooltip"
                place="top"
                content="Jedná se o období, po které budete držet investice. U pravidelné investice je to zároveň počet let po které budete zasílat měsíční platbu v pravidelné výši."
                className="tooltip"
            />
            <ReactTooltip
                id="shares-tooltip"
                place="top"
                content="Zde se doplňují údaje o složení portfolia, jehož historické statistiky chcete zobrazit. V této částu konkrétně v procentech část portfolia, která má expozici na vývoj akciových trhů."
                className="tooltip"
            />
            <ReactTooltip
                id="bonds-tooltip"
                place="top"
                content="Zde se doplňují údaje o složení portfolia, jehož historické statistiky chcete zobrazit. V této částu konkrétně v procentech část portfolia, která má expozici na vývoj cen dluhopisů."
                className="tooltip"
            />
            <ReactTooltip
                id="rest-tooltip"
                place="top"
                content="Dopočte se automaticky do 100 % portfolia ze zadaných podílů akcií a dluhopisů."
                className="tooltip"
            />
            <ReactTooltip
                id="type-tooltip"
                place="top"
                content="Můžete zvolit jednorázovou investici či pravidelnou měsíční investici. Jednorázová investice nese vyšší rizika špatného načasování nákupu investic, pravidelná tento problém naopak eliminuje průměrováním nákupní ceny investice – jednou koupíte draze, jindy levně a celkově se to rozumně zprůměruje. Určitá nevýhoda pravidelné investice za předpokladu, že do ní rozpouštíte kapitál, který byste mohli investovat ihned, je v tom, že se kapitál zhodnocuje prakticky jen poloviční dobu proti investici pravidelné (první investovaná částka po celý horizont, ta poslední jen měsíc apod.)."
                className="tooltip"
            />
            <ReactTooltip
                id="results-type-tooltip"
                place="top"
                content="Hodnota určité části dnes není stejná jako před dávným obdobím s ohledem na růst cenové hladiny. Výsledky si tedy můžete zobrazit nominálně, tj. bez očištění vlivu inflace; či reálně, tj. očištěné o vliv inflace."
                className="tooltip"
            />

            <ReactTooltip
                id="nr-of-simulations-tooltip"
                place="top"
                content="Počet simulací, které jsme z dostupné časové řady po historických měsíčních datech provedli."
                className="tooltip"
            />

            <ReactTooltip
                id="median-tooltip"
                place="top"
                content="Medián jako střední hodnota průměrných ročních výnosů (p.a.) dle zvoleného zadání z provedených simulací."
                className="tooltip"
            />

            <ReactTooltip
                id="loss-simulations-tooltip"
                place="top"
                content={`Vyjadřuje, kolik procent simulací z celkového počtu skončilo na konci horizontu ${lastUsedInputData?.accountForInflation ? 'reálnou' : 'nominální'} ztrátou.`}
                className="tooltip"
                key={lastUsedInputData?.accountForInflation+"-loss-simulations-tooltip"}
            />

            <ReactTooltip
                id="percentile-range-tooltip"
                place="top"
                content={`Interval tvořený 1% a 99% percentilem. Nachází se v něm tedy 98 % hodnot. Uvedená čísla znamenají, že 99 % všech simulací skončilo průměrným ročním výnosem vyšším či rovném než ${Math.round((percentiles?.perc1 ?? 0) * 10000)/100} % a 1 % všech simulací skončilo průměrným ročním výnosem vyšším či rovném než ${Math.round((percentiles?.perc99 ?? 0) * 10000)/100} %`}
                className="tooltip"
                key={lastUsedInputData?.accountForInflation+"-percentile-range-tooltip"}
            />

            <ReactTooltip
                id="ten-percentile-range-tooltip"
                place="top"
                content={`Interval tvořený 10% a 90% percentilem. Nachází se v něm tedy 80 % hodnot. Uvedená čísla znamenají, že 90 % všech simulací skončilo průměrným ročním výnosem vyšším či rovném než ${Math.round((percentiles?.perc10 ?? 0) * 10000)/100} % a 10 % všech simulací skončilo průměrným ročním výnosem vyšším či rovném než ${Math.round((percentiles?.perc90 ?? 0) * 10000)/100} %`}
                className="tooltip"
                key={lastUsedInputData?.accountForInflation+"-ten-percentile-range-tooltip"}
            />

            <ReactTooltip
                id="quartile-range-tooltip"
                place="top"
                content={`Interval tvořený 25% a 75% percentilem. Nachází se v něm tedy 50 % hodnot. Uvedená čísla znamenají, že 75 % všech simulací skončilo průměrným ročním výnosem vyšším či rovném než ${Math.round((percentiles?.kvartil1 ?? 0) * 10000)/100} % a 25 % všech simulací skončilo průměrným ročním výnosem vyšším či rovném než ${Math.round((percentiles?.kvartil3 ?? 0) * 10000)/100} %`}
                className="tooltip"
                key={lastUsedInputData?.accountForInflation+"-quartile-range-tooltip"}
            />
        </>
    </>)
});